import { CheckCircle2, CircleAlert, Landmark, ReceiptText } from "lucide-react";

import { StatePill } from "@/routes/_app/-components/financeiro/financeiro-v2/primitives";
import type { CardSettlementDetail } from "@/types/financeiro";

export function SettlementSummary({ settlement }: { settlement: CardSettlementDetail }) {
	const bankMatched =
		settlement.bank_transaction_id !== null &&
		settlement.bank_status === "posted" &&
		settlement.bank_removed_remote === false &&
		settlement.bank_amount_cents === settlement.net_cents;
	const componentsMatched =
		settlement.credit_orders.length > 0 && settlement.difference_cents === 0;
	const salesIdentified =
		settlement.sale_count > 0 &&
		settlement.incomplete_credit_order_count === 0 &&
		settlement.unidentified_sale_count === 0 &&
		settlement.reversed_sale_count === 0 &&
		settlement.chargeback_sale_count === 0;
	const bankOpenLabel = settlement.status === "paid" ? "Sem vínculo" : "Ainda não paga";
	const componentsOpenLabel =
		settlement.credit_orders.length === 0 ? "Sem detalhamento" : "Valores divergentes";
	const ordersMatchedLabel =
		settlement.credit_orders.length === 1
			? "1 ordem conferida"
			: `${settlement.credit_orders.length} ordens conferidas`;
	const salesMatchedLabel =
		settlement.sale_count === 1
			? "1 venda identificada"
			: `${settlement.sale_count} vendas identificadas`;
	let salesOpenLabel = `${settlement.unidentified_sale_count} sem paciente`;

	if (settlement.sale_count === 0) {
		salesOpenLabel = "Vendas não encontradas";
	}

	if (settlement.incomplete_credit_order_count > 0) {
		salesOpenLabel =
			settlement.incomplete_credit_order_count === 1
				? "1 ordem incompleta"
				: `${settlement.incomplete_credit_order_count} ordens incompletas`;
	}

	if (settlement.reversed_sale_count > 0) {
		salesOpenLabel =
			settlement.reversed_sale_count === 1
				? "1 venda cancelada"
				: `${settlement.reversed_sale_count} vendas canceladas`;
	}

	if (settlement.chargeback_sale_count > 0) {
		salesOpenLabel = `${settlement.chargeback_sale_count} com chargeback`;
	}

	return (
		<div className="grid gap-3 sm:grid-cols-3">
			<SettlementSummaryItem
				icon={Landmark}
				label="Entrada bancária"
				matched={bankMatched}
				matchedLabel="Vinculada"
				openLabel={bankOpenLabel}
			/>
			<SettlementSummaryItem
				icon={ReceiptText}
				label="Fechamento da Rede"
				matched={componentsMatched}
				matchedLabel={ordersMatchedLabel}
				openLabel={componentsOpenLabel}
			/>
			<SettlementSummaryItem
				icon={CheckCircle2}
				label="Pacientes"
				matched={salesIdentified}
				matchedLabel={salesMatchedLabel}
				openLabel={salesOpenLabel}
			/>
		</div>
	);
}

function SettlementSummaryItem({
	icon: Icon,
	label,
	matched,
	matchedLabel,
	openLabel,
}: {
	icon: typeof Landmark;
	label: string;
	matched: boolean;
	matchedLabel: string;
	openLabel: string;
}) {
	const stateLabel = matched ? matchedLabel : openLabel;
	const tone = matched ? ("green" as const) : ("amber" as const);

	return (
		<div className="rounded-lg border border-border bg-background p-3">
			<div className="flex items-center justify-between gap-3">
				<Icon className="size-4 text-primary" />
				<StatePill label={stateLabel} tone={tone} />
			</div>
			<p className="mt-3 text-xs font-semibold text-foreground">{label}</p>
			{!matched && <CircleAlert className="mt-2 size-3.5 text-warning" />}
		</div>
	);
}
